import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import { ToastContext } from '@weezevent/nacre';

import { Segment, Grid, Table, Statistic, Header } from 'semantic-ui-react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';

import AttendeeRefundRequest from '../../../models/attendeeRefundRequest';

import Translator from '../../../utils/translator';

const DateFormatter = organization => new Intl.DateTimeFormat([organization.defaultLanguage() || 'fr-FR', 'fr-FR']);

const leftpad = s => `00${s}`.slice(-2);

const formatter = date => {
    let y = date.getFullYear();
    let d = leftpad(date.getDate());
    let m = leftpad(date.getMonth() + 1);
    return `${y}-${m}-${d}`;
};

const build_data_line = (refund_requests, locale) => {
    let dates = {};
    for (let request of refund_requests) {
        let date = new Date(request.created);
        let label = formatter(date);
        dates[label] = dates[label] || [];
        dates[label].push(request);
    }

    let body = [
        {
            id: 'refund-requests',
            color: '#0032fa',
            data: []
        }
    ];

    let lastDate = Date.now();
    let startDate = new Date(Object.keys(dates)[0]);
    for (
        let currentDate = startDate, currentDateFormatter = formatter(currentDate);
        currentDate <= lastDate;
        currentDate.setDate(currentDate.getDate() + 1), currentDateFormatter = formatter(currentDate)
    ) {
        body[0].data.push({
            x: currentDate.toLocaleDateString(locale),
            y: dates[currentDateFormatter] ? dates[currentDateFormatter].length : 0
        });
    }

    return body;
};

const build_data_bar = (refund_requests, locale = 'fr-FR') => {
    let rates = {};
    for (let request of refund_requests) {
        rates[request.attendee.rate_id] = rates[request.attendee.rate_id] || {
            rate: Translator(request.attendee.rate_name, locale),
            count: 0
        };

        rates[request.attendee.rate_id].count++;
    }

    return Object.values(rates);
};

function RefundRequestRow({ refundRequest, organization }) {
    let formatter = DateFormatter(organization);
    let mail = refundRequest.attendee.order.buyer_email;
    return (
        <Table.Row>
            <Table.Cell>{formatter.format(new Date(refundRequest.created))}</Table.Cell>
            <Table.Cell>{`${refundRequest.attendee.first_name} ${refundRequest.attendee.last_name}`}</Table.Cell>
            <Table.Cell>
                <a href={`mailto:${mail}`}>{mail}</a>
            </Table.Cell>
            <Table.Cell>{refundRequest.event_name}</Table.Cell>
            <Table.Cell>{Translator(refundRequest.attendee.rate_name, organization.defaultLanguage() || 'fr-FR')}</Table.Cell>
        </Table.Row>
    );
}

RefundRequestRow.propTypes = {
    refundRequest: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};

function RefundsByDayGraph({ refundRequests, organization }) {
    let data = refundRequests ? build_data_line(refundRequests, organization.defaultLanguage() || 'fr-FR') : [];
    const { t } = useTranslation();
    return (
        <ResponsiveLine
            isInteractive
            useMesh
            enableGridX={false}
            enableGridY={false}
            enablePointLabel={true}
            colors={['#0032fa']}
            pointSize={10}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            data={data}
            axisBottom={{
                legend: t('ticket.stats.event.refund_request.legend.bottom'),
                // format: '%b %d %Y',
                //tickvalues: 'every month',
                legendOffset: 40,
                legendPosition: 'middle'
            }}
            axisLeft={{
                legend: t('ticket.stats.event.refund_request.legend.left'),
                legendOffset: -40,
                legendPosition: 'middle',
                tickValues: 1
            }}
            // xScale={{
            //     type: 'time',
            //     format: '%Y-%m-%d',
            //     precision: 'day'
            // }}
            // xFormat={`time:%Y-%m-%d`}
            margin={{ top: 40, right: 40, bottom: 60, left: 60 }}
        />
    );
}

RefundsByDayGraph.propTypes = {
    refundRequests: PropTypes.array.isRequired,
    organization: PropTypes.object.isRequired
};

function RatesRefunded({ refundRequests, organization }) {
    let data = refundRequests ? build_data_bar(refundRequests, organization.defaultLanguage() || 'fr-FR') : [];
    const { t } = useTranslation();
    return (
        <ResponsiveBar
            isInteractive
            useMesh
            data={data}
            colors={['#0032fa']}
            keys={['count']}
            indexBy="rate"
            axisBottom={{
                legend: t('ticket.stats.event.refund_request.legend.ratesBottom'),
                legendOffset: 40,
                legendPosition: 'middle'
            }}
            axisLeft={{
                legend: t('ticket.stats.event.refund_request.legend.ratesLeft'),
                legendOffset: -40,
                legendPosition: 'middle'
            }}
            margin={{ top: 40, right: 40, bottom: 60, left: 60 }}
        />
    );
}

RatesRefunded.propTypes = {
    refundRequests: PropTypes.array.isRequired,
    organization: PropTypes.object.isRequired
};

export default function RefundRequestsStats({ organization, history }) {
    const toast = React.useContext(ToastContext);
    const [refundRequests, setRefundRequests] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const { t } = useTranslation();
    // Get refund requests from server
    React.useEffect(() => {
        setLoading(true);
        AttendeeRefundRequest.getAll({
            organizationId: organization.id
        })
            .then(refund_requests => {
                setRefundRequests(refund_requests);
                setLoading(false);
            })
            .catch(e => {
                toast.error(t('ticket.stats.event.refund_request.retrievalError'));
            });
    }, []);

    if (loading) {
        return <Segment basic>Loading...</Segment>;
    }

    let differentOrders = new Set(refundRequests.map(refund_request => refund_request.attendee.order.id_transaction)).size;

    let dateText = t('ticket.stats.event.refund_request.table.header.date');

    return (
        <React.Fragment>
            <Header as="h1">{t('ticket.stats.event.refund_request.title')}</Header>
            <Segment basic>
                <Grid columns={2} stackable style={{ textAlign: 'center' }}>
                    <Grid.Column>
                        <Statistic size="large">
                            <Statistic.Label>{t('ticket.common.orders')}</Statistic.Label>
                            <Statistic.Value>{differentOrders}</Statistic.Value>
                        </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                        <Statistic size="large">
                            <Statistic.Label>{t('ticket.common.refund_requests')}</Statistic.Label>
                            <Statistic.Value>{refundRequests.length}</Statistic.Value>
                        </Statistic>
                    </Grid.Column>
                </Grid>

                <Segment style={{ height: '400px' }}>
                    <RefundsByDayGraph organization={organization} refundRequests={refundRequests} />
                </Segment>

                <Segment style={{ height: '400px' }}>
                    <RatesRefunded organization={organization} refundRequests={refundRequests} />
                </Segment>

                <Segment>
                    <div style={{ overflowX: 'scroll' }}>
                        <Table unstackable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>{dateText}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('ticket.stats.event.refund_request.table.header.name')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('ticket.stats.event.refund_request.table.header.email')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('ticket.stats.event.refund_request.table.header.event')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('ticket.stats.event.refund_request.table.header.rate')}</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {refundRequests.map((refund_request, i) => (
                                    <RefundRequestRow key={i} refundRequest={refund_request} organization={organization} />
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </Segment>
            </Segment>
        </React.Fragment>
    );
}

RefundRequestsStats.propTypes = {
    organization: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired
};
